/**
 * PDP Video Gallery
 *
 * Each video has its own hero item with a placeholder and iframe slot.
 * Iframes stay in place permanently — CSS toggles visibility so
 * playback persists across breakpoint changes.
 *
 * Desktop: active hero item visible (left), sidebar thumbnails (right).
 * Mobile: all hero items stacked full-width, sidebar hidden.
 */

const VIDEO_ID_PATTERNS = { youtube: /^[\w-]{11}$/, vimeo: /^\d+$/ };

function isValidVideoId(provider, id) {
  const pattern = VIDEO_ID_PATTERNS[provider] || VIDEO_ID_PATTERNS.youtube;
  return pattern.test(id || '');
}

class PdpVideoGallery extends HTMLElement {
  connectedCallback() {
    this.heroContainer = this.querySelector('[data-hero]');
    this.heroItems = [...this.querySelectorAll('[data-hero-item]')];
    this.sidebarItems = [...this.querySelectorAll('[data-video-item]')];
    this.activeVideoId = this.heroItems[0]?.dataset.videoId || null;
    this.abortController = new AbortController();

    this.bindEvents();
    this.setHeroPosters();
    this.fetchVideoMeta();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  bindEvents() {
    const { signal } = this.abortController;

    // Hero placeholder clicks (delegated)
    if (this.heroContainer) {
      this.heroContainer.addEventListener('click', (e) => {
        const playBtn = e.target.closest('.pdp-video-gallery__play-btn');
        if (!playBtn) return;

        const placeholder = playBtn.closest('.pdp-video-gallery__placeholder');
        if (placeholder) {
          this.playVideo(placeholder.dataset.videoId);
        }
      }, { signal });
    }

    // Sidebar thumbnail clicks + keyboard support
    this.sidebarItems.forEach((item) => {
      const thumbnail = item.querySelector('[data-thumbnail]');
      if (!thumbnail) return;

      thumbnail.addEventListener('click', () => {
        this.playVideo(item.dataset.videoId);
      }, { signal });

      thumbnail.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.playVideo(item.dataset.videoId);
        }
      }, { signal });
    });
  }

  setHeroPosters() {
    this.querySelectorAll('.pdp-video-gallery__placeholder[data-video-id]').forEach((placeholder) => {
      const id = placeholder.dataset.videoId;
      const provider = placeholder.dataset.videoProvider || 'youtube';
      if (provider !== 'youtube') return;
      if (!isValidVideoId(provider, id)) return;

      const fallback = () => {
        placeholder.style.backgroundImage = `url('https://img.youtube.com/vi/${id}/hqdefault.jpg')`;
      };
      const probe = new Image();
      probe.onerror = fallback;
      probe.onload = function () {
        if (this.naturalWidth <= 120) fallback();
      };
      probe.src = `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;
    });
  }

  playVideo(videoId) {
    this.activateVideo(videoId);

    // Stop all other videos
    this.heroItems.forEach((h) => {
      if (h.dataset.videoId !== videoId) {
        const iframe = h.querySelector('.pdp-video-gallery__iframe');
        if (iframe) iframe.remove();
        h.classList.remove('pdp-video-gallery__hero-item--playing');
      }
    });

    const heroItem = this.heroItems.find((h) => h.dataset.videoId === videoId);
    if (!heroItem) return;

    if (!heroItem.querySelector('.pdp-video-gallery__iframe')) {
      const wrapper = heroItem.querySelector('.pdp-video-gallery__hero-wrapper');
      const iframe = this.createIframe(videoId);
      if (wrapper && iframe) {
        wrapper.appendChild(iframe);
      }
    }

    heroItem.classList.add('pdp-video-gallery__hero-item--playing');
  }

  activateVideo(videoId) {
    this.activeVideoId = videoId;

    this.heroItems.forEach((h) => {
      h.classList.toggle('pdp-video-gallery__hero-item--active', h.dataset.videoId === videoId);
    });

    this.sidebarItems.forEach((s) => {
      s.classList.toggle('pdp-video-gallery__sidebar-item--active', s.dataset.videoId === videoId);
    });
  }

  createIframe(videoId) {
    const index = this.heroItems.findIndex((h) => h.dataset.videoId === videoId);
    const provider = this.heroItems[index]?.dataset.videoProvider || 'youtube';
    if (!isValidVideoId(provider, videoId)) return null;
    const iframe = document.createElement('iframe');
    iframe.className = 'pdp-video-gallery__iframe';
    iframe.src =
      provider === 'vimeo'
        ? `https://player.vimeo.com/video/${videoId}?autoplay=1`
        : `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`;
    iframe.title = `Video ${index + 1}`;
    iframe.setAttribute('frameborder', '0');
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
    iframe.allowFullscreen = true;
    return iframe;
  }

  fetchVideoMeta() {
    const allItems = [...this.heroItems, ...this.sidebarItems];
    const fetched = new Set();

    allItems.forEach((item) => {
      const videoId = item.dataset.videoId;
      const provider = item.dataset.videoProvider || 'youtube';
      if (!videoId || fetched.has(videoId)) return;
      if (!isValidVideoId(provider, videoId)) return;
      fetched.add(videoId);

      const endpoint =
        provider === 'vimeo'
          ? `https://vimeo.com/api/oembed.json?url=${encodeURIComponent('https://vimeo.com/' + videoId)}&width=1280`
          : `https://noembed.com/embed?url=${encodeURIComponent('https://www.youtube.com/watch?v=' + videoId)}`;

      fetch(endpoint)
        .then((res) => res.json())
        .then((data) => {
          if (data.title) {
            this.querySelectorAll(`[data-video-id="${videoId}"] [data-video-title]`).forEach((el) => {
              el.textContent = data.title;
            });
          }
          if (provider === 'vimeo' && data.thumbnail_url) {
            this.setVimeoThumbnails(videoId, data.thumbnail_url);
          }
        })
        .catch(() => {});
    });
  }

  setVimeoThumbnails(videoId, url) {
    this.querySelectorAll(`.pdp-video-gallery__placeholder[data-video-id="${videoId}"]`).forEach((placeholder) => {
      placeholder.style.backgroundImage = `url('${url}')`;
    });
    this.querySelectorAll(`[data-video-id="${videoId}"] [data-thumb]`).forEach((img) => {
      img.src = url;
    });
  }
}

if (!customElements.get('pdp-video-gallery')) {
  customElements.define('pdp-video-gallery', PdpVideoGallery);
}
